/**
 * cart-mystery-upsell.js
 *
 * Dedicated Mystery Wooble cart upsell for AMP SlideCart.
 *
 * Owns:
 * - rendering the configured Mystery Wooble upsell, including while it is
 *   already in cart (repeat paid purchases remain separate from tagged gifts)
 * - Mystery-only add-to-cart handling for the custom upsell button
 * - "Unlocked!" treatment for Mystery Wooble
 * - "A random exclusive kit" treatment for Mystery Exclusive Wooble
 * - Mystery Exclusive Wooble discount-icon cleanup
 * - re-rendering when AMP replaces/updates cart markup
 *
 * Does NOT own:
 * - cart open/close
 * - generic add/remove/quantity mechanics
 * - generic storefront ATC interception
 * - reward/threshold logic
 * - GWP qualification
 * - old WBLS-93 / WBLS-95 behavior
 *
 * Dependencies:
 * 1. _proteus-utils.js must load first and expose
 *      window.WooblesUtils.observeSelector
 *
 * 2. theme.liquid must define
 *      window.WooblesCartConfig.mysteryUpsell
 *
 * 3. The merged cart-gwp.js must expose window.WooblesCartGWP.runCartTransaction.
 */
(() => {
  'use strict';
  const isCustomCart = () => Boolean(document.querySelector('cart-drawer[data-woobles-custom-cart]'));
  if (window.__WOOBLES_CART_MYSTERY_UPSELL_INIT__) return;
  const observeSelector = window.WooblesUtils?.observeSelector || window.observeSelector;
  if (!isCustomCart() && typeof observeSelector !== 'function') {
    console.error('[CartMysteryUpsell] observeSelector unavailable. Ensure _proteus-utils.js loads first.');
    return;
  }
  window.__WOOBLES_CART_MYSTERY_UPSELL_INIT__ = true;
  const TARGET_SELECTOR = ['#slidecarthq .js-custom-upsell', '.slidecarthq .js-custom-upsell'].join(', ');
  const ITEMS_SELECTOR = ['#slidecarthq .items', '.slidecarthq .items'].join(', ');
  const LINE_ITEM_SELECTOR = ['#slidecarthq .items .item', '.slidecarthq .items .item'].join(', ');
  const MYSTERY_ATC_SELECTOR = '[data-mystery-upsell-atc]';
  const DISCOUNTED_VARIANT_ID = 44416648118438;
  const CART_UPSELL_PROPERTY = '_mystery_cart_upsell';
  const CART_UPSELL_DISCOUNT_PERCENT = 10;
  const CART_UPSELL_DISCOUNT_CODE = 'MYSTERY10';
  let mysteryAddInProgress = false;
  let mysteryUpsellError = '';
  const MYSTERY_WOOBLE = 'mystery wooble';
  const MYSTERY_EXCLUSIVE_WOOBLE = 'mystery exclusive wooble';
  const normalize = (value) =>
    String(value || '')
      .trim()
      .toLowerCase();
  const isMysteryTitle = (title) => {
    const normalized = normalize(title);
    return normalized === MYSTERY_WOOBLE || normalized === MYSTERY_EXCLUSIVE_WOOBLE;
  };
  const escapeHtml = (value) =>
    String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  /* ---------------------------------------------
   * Theme configuration
   * ------------------------------------------- */
  const getMysteryConfig = () => {
    const config = window.WooblesCartConfig?.mysteryUpsell;
    if (!config?.enabled) return null;
    const mystery = {
      productId: Number(config.productId),
      variantId: Number(config.variantId),
      image: config.image || '',
      title: config.title || '',
      price: Number(config.price),
      regularPrice: Number(config.regularPrice),
      url: config.url || '',
    };
    if (
      !Number.isFinite(mystery.productId) ||
      mystery.productId <= 0 ||
      !Number.isFinite(mystery.variantId) ||
      mystery.variantId <= 0 ||
      !mystery.title ||
      !isMysteryTitle(mystery.title)
    ) {
      return null;
    }
    return mystery;
  };
  /* ---------------------------------------------
   * Current AMP cart DOM state
   * ------------------------------------------- */
  const cartItemCount = () => document.querySelectorAll(LINE_ITEM_SELECTOR).length;
  /* ---------------------------------------------
   * Rendering
   * ------------------------------------------- */
  const formatPrice = (price) => {
    if (!Number.isFinite(price)) return '';
    return Number.isInteger(price) ? String(price) : price.toFixed(2);
  };
  const getPriceHtml = (mystery) => {
    if (!Number.isFinite(mystery.price)) return '';
    if (mystery.variantId !== DISCOUNTED_VARIANT_ID) {
      return `$${escapeHtml(formatPrice(mystery.price))}`;
    }
    // config.price is the advertised cart-upsell price ($27).
    // theme.liquid should also supply regularPrice ($30).
    const regularPrice =
      Number.isFinite(mystery.regularPrice) && mystery.regularPrice > 0
        ? mystery.regularPrice
        : Math.round((mystery.price / (1 - CART_UPSELL_DISCOUNT_PERCENT / 100)) * 100) / 100;
    return `<s>$${escapeHtml(formatPrice(regularPrice))}</s> $${escapeHtml(formatPrice(mystery.price))}`;
  };
  const renderMysteryUpsell = (mystery) => {
    const normalizedTitle = normalize(mystery.title);
    const unlockedHtml =
      normalizedTitle === MYSTERY_WOOBLE ? '<div class="js-unlocked-label unlocked-label">Unlocked!</div>' : '';
    const exclusiveNoteHtml =
      normalizedTitle === MYSTERY_EXCLUSIVE_WOOBLE
        ? '<div class="js-mystery-wooble-note mystery-wooble-note">A random exclusive kit</div>'
        : '';
    return `
      <div
        data-slidecart-product-id="${escapeHtml(mystery.productId)}"
        class="custom-upsell js-custom-upsell-item js-mystery-upsell-item"
      >
        <div class="custom-upsell__inner">
          <div class="custom-upsell__image">
            <img
              src="${escapeHtml(mystery.image)}"
              alt="${escapeHtml(mystery.title)}"
              loading="lazy"
            />
          </div>
          <div class="custom-upsell__content">
            ${unlockedHtml}
            <div class="custom-upsell__title">
              ${escapeHtml(mystery.title)}
            </div>
            ${exclusiveNoteHtml}
            <div class="custom-upsell__info">
              <a
                href="#"
                role="button"
                class="custom-upsell__button js-cart--upsell-item--atc"
                data-js-atc="${escapeHtml(mystery.variantId)}"
                data-mystery-upsell-atc
              >
                Add
              </a>
              <div class="custom-upsell__price">
                ${getPriceHtml(mystery)}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  };
  const renderUpsell = () => {
    const target = document.querySelector(TARGET_SELECTOR);
    if (!target) return;
    const mystery = getMysteryConfig();
    // Match current storefront behavior: do not show the custom Mystery
    // upsell when the cart itself is empty.
    if (!mystery || cartItemCount() === 0) {
      target.innerHTML = mysteryUpsellError
        ? `<p class="mystery-upsell__error" role="alert">${escapeHtml(mysteryUpsellError)}</p>`
        : '';
      return;
    }
    target.style.display = 'grid';
    target.innerHTML = renderMysteryUpsell(mystery);
    setMysteryAtcLoading(target.querySelector(MYSTERY_ATC_SELECTOR), mysteryAddInProgress);
    if (mysteryUpsellError) {
      target.insertAdjacentHTML(
        'beforeend',
        `<p class="mystery-upsell__error" role="alert">${escapeHtml(mysteryUpsellError)}</p>`,
      );
    }
  };
  /* ---------------------------------------------
   * Mystery-only add-to-cart
   * ------------------------------------------- */
  const getExistingDiscountCodes = (cart) => {
    const codes = new Map();
    const addCode = (code) => {
      const value = String(code || '').trim();
      if (value) codes.set(value.toUpperCase(), value);
    };
    // Some responses expose submitted codes, including currently ineligible ones.
    (cart.discount_codes || []).forEach((entry) => addCode(typeof entry === 'string' ? entry : entry.code));
    // Recover applied product and order codes from the documented allocations.
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.discount_applications || []),
      ...(cart.items || []).flatMap((item) =>
        (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application),
      ),
    ];
    applications.forEach((application) => {
      if (application?.type === 'discount_code') addCode(application.code || application.title);
    });
    return codes;
  };
  const applyMysteryDiscount = async (cartApi) => {
    const cart = await cartApi.readCart();
    const codes = getExistingDiscountCodes(cart);
    codes.set(CART_UPSELL_DISCOUNT_CODE, CART_UPSELL_DISCOUNT_CODE);
    const updatedCart = await cartApi.updateCart({ discount: [...codes.values()].join(',') });
    const matchingItems = (updatedCart.items || []).filter(
      (item) =>
        Number(item.variant_id || item.id) === DISCOUNTED_VARIANT_ID &&
        item.properties?.[CART_UPSELL_PROPERTY] === 'true' &&
        !item.properties?._gwp &&
        item.properties?.['_gwp-checkout-ui-gift'] !== 'true',
    );
    const hasDiscount =
      matchingItems.length > 0 &&
      matchingItems.every((item) =>
        (item.line_level_discount_allocations || []).some(
          (allocation) =>
            normalize(allocation.discount_application?.code || allocation.discount_application?.title) ===
              normalize(CART_UPSELL_DISCOUNT_CODE) && Number(allocation.amount) > 0,
        ),
      );
    if (!hasDiscount) {
      throw new Error(
        'MYSTERY10 did not allocate a discount to the Mystery upsell. Check eligibility and discount combinations.',
      );
    }
    return updatedCart;
  };
  const addMysteryToCart = async (variantId, cartApi) => {
    const item = { id: Number(variantId), quantity: 1 };
    // Keep the upsell line identifiable. The native MYSTERY10 code targets
    // eligible merchandise; it does not enforce this property as a condition.
    if (item.id === DISCOUNTED_VARIANT_ID) {
      item.properties = { [CART_UPSELL_PROPERTY]: 'true' };
    }
    return cartApi.addItems([item]);
  };
  const setMysteryAtcLoading = (button, isLoading) => {
    if (!button) return;
    const usesSpinner = button.classList.contains('gwp-add-button');
    button.classList.toggle('is-loading', usesSpinner && isLoading);
    if (isLoading) {
      button.dataset.loading = 'true';
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      button.textContent = usesSpinner ? 'Add' : 'Adding…';
      return;
    }
    delete button.dataset.loading;
    button.removeAttribute('aria-disabled');
    button.removeAttribute('aria-busy');
    button.textContent = 'Add';
  };
  document.addEventListener(
    'click',
    async (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button =
        target.closest(MYSTERY_ATC_SELECTOR) ||
        (isCustomCart() ? target.closest('[data-gwp-upsell] [data-gwp-add]') : null);
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (
        mysteryAddInProgress ||
        button.dataset.loading === 'true' ||
        button.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }
      const variantId = Number(button.dataset.jsAtc || button.dataset.variantId);
      if (!Number.isFinite(variantId) || variantId <= 0) {
        console.error('[CartMysteryUpsell] Missing or invalid Mystery variant ID.');
        return;
      }
      setMysteryAtcLoading(button, true);
      mysteryAddInProgress = true;
      mysteryUpsellError = '';
      let itemAdded = false;
      try {
        const manager = window.WooblesCartGWP;
        if (typeof manager?.runCartTransaction !== 'function') {
          throw new Error('The merged cart-gwp.js must load before adding the Mystery upsell.');
        }
        await manager.runCartTransaction(async (cartApi) => {
          await addMysteryToCart(variantId, cartApi);
          itemAdded = true;
          if (variantId === DISCOUNTED_VARIANT_ID) {
            await applyMysteryDiscount(cartApi);
          }
        });
      } catch (error) {
        console.error('[CartMysteryUpsell] Mystery upsell request failed:', error);
        mysteryUpsellError =
          error?.code === 'CART_BUSY'
            ? 'Your cart is still updating. Please try again in a moment.'
            : itemAdded
              ? 'Mystery kit added, but MYSTERY10 could not be confirmed. Check the price in your cart before checkout.'
              : 'The Mystery kit could not be added. Please try again.';
        if (!itemAdded) setMysteryAtcLoading(button, false);
      } finally {
        mysteryAddInProgress = false;
        setMysteryAtcLoading(button, false);
        // A discount failure never retries the add; show the warning after refresh.
        if (isCustomCart()) {
          const status = document.querySelector('[data-mystery-upsell-status]');
          if (status) status.textContent = mysteryUpsellError;
        } else renderUpsell();
      }
    },
    true,
  );
  /* ---------------------------------------------
   * Mystery Exclusive cart-line treatment
   * ------------------------------------------- */
  const cleanMysteryExclusiveDiscountIcon = (discountAllocation) => {
    const lineItem = discountAllocation.closest('.item');
    if (!lineItem) return;
    const titleEl = lineItem.querySelector('.title a') || lineItem.querySelector('.title');
    if (normalize(titleEl?.textContent) !== MYSTERY_EXCLUSIVE_WOOBLE) {
      return;
    }
    const iconWrapper = discountAllocation.querySelector('.discount-allocations-flex-initial');
    const iconSvg = discountAllocation.querySelector('.discount-allocations-tags-icon');
    if (iconWrapper) iconWrapper.style.display = 'none';
    if (iconSvg) iconSvg.style.display = 'none';
  };
  if (!isCustomCart())
    observeSelector(
      ['#slidecarthq .item .discount-allocations', '.slidecarthq .item .discount-allocations'].join(', '),
      cleanMysteryExclusiveDiscountIcon,
    );
  /* ---------------------------------------------
   * AMP lifecycle
   * ------------------------------------------- */
  // Render when AMP creates/replaces the custom HTML target.
  if (!isCustomCart())
    observeSelector(TARGET_SELECTOR, () => {
      renderUpsell();
    });
  // AMP may keep the same .items wrapper and mutate its children as cart
  // items are added/removed. Attach one observer to each wrapper instance.
  if (!isCustomCart())
    observeSelector(ITEMS_SELECTOR, (items) => {
      if (items.dataset.mysteryUpsellObserverAttached === 'true') {
        renderUpsell();
        return;
      }
      items.dataset.mysteryUpsellObserverAttached = 'true';
      const observer = new MutationObserver(() => {
        renderUpsell();
      });
      observer.observe(items, {
        childList: true,
        subtree: true,
      });
      renderUpsell();
    });
})();
